import {
	Whatshot as FireIcon,
	SavingsOutlined as SavingsIcon,
	ReceiptOutlined as ReceiptIcon,
	ShieldOutlined as CoverageIcon,
} from "@mui/icons-material";

import {
	Box,
	Button,
	Card,
	CardContent,
	CircularProgress,
	Divider,
	List,
	ListItem,
	ListItemIcon,
	ListItemText,
	Typography,
	useTheme,
} from "@mui/material";

import { globalFormatCurrencyBRL } from "../../../../shared/utils";
import { useAnimateNumber } from "../../hooks/useAnimateNumber";
import { NormalizedCoverage } from "../utils/normalizeCoverage";

interface FireSummaryCardProps {
	coverages: NormalizedCoverage[];
	totalQuotation: number;
	discount?: number;
	isLoading?: boolean;

	// Botões
	onSave?: () => void;
	onContract?: () => void;

	saveLabel?: string;

	// Parcelas
	installments?: number;

	/**
	 * ⬅️ CONTROLE REAL do botão "Salvar"
	 * - validado externamente via Yup
	 * - validado no container (form pronto, coverages etc.)
	 */
	canSaveExternal?: boolean;

	/**
	 * ⬅️ CONTROLE REAL do botão "Contratar"
	 * Deve vir do container (id existente, salvo, sem erros)
	 */
	canContract?: boolean;
}

export const FireSummaryCard = ({
	coverages = [],
	totalQuotation,
	discount = 0,
	isLoading = false,
	onSave,
	onContract,
	saveLabel = "Salvar Cotação",
	installments = 1,
	canSaveExternal = true,
	canContract = false,
}: FireSummaryCardProps) => {

	const theme = useTheme();

	// 🔥 Total calculado
	const animatedTotal = useAnimateNumber(totalQuotation - discount);

	// 🔢 Valor das parcelas
	const animatedInstallment = useAnimateNumber(
		installments > 0 ? (totalQuotation - discount) / installments : 0
	);

	const hasCoverages = coverages.length > 0;
	const hasTotal = totalQuotation > 0;

	// Validação do botão salvar
	const canSave =
		hasCoverages &&
		hasTotal &&
		!isLoading &&
		canSaveExternal;

	return (
		<Card
			sx={{
				width: "100%",
				maxWidth: 600,
				boxShadow: "0 4px 20px rgba(0,0,0,0.05)",
				borderRadius: 2,
				overflow: "hidden",
			}}
		>
			{/* Header */}
			<Box
				sx={{
					backgroundColor: theme.palette.primary.main,
					color: theme.palette.primary.contrastText,
					p: 2,
				}}
			>
				<Typography variant="h5" fontWeight="bold">
					Resumo da Cotação
				</Typography>
				<Typography variant="body2">Coberturas e valores apurados</Typography>
			</Box>

			<CardContent sx={{ p: 0 }}>
				<List sx={{ py: 2 }}>
					{coverages.map((item, index) => (
						<ListItem key={index} disableGutters sx={{ px: 2 }}>
							<ListItemIcon sx={{ minWidth: 40, color: "primary.main" }}>
								{item.name?.toLowerCase()?.includes("incêndio") ? (
									<FireIcon />
								) : (
									<CoverageIcon />
								)}
							</ListItemIcon>

							<ListItemText primary={item.name} />

							<Typography fontWeight="bold">
								{globalFormatCurrencyBRL(item.coverage_value?.toString() || "0")}
							</Typography>
						</ListItem>
					))}

					{discount > 0 && (
						<ListItem disableGutters sx={{ px: 2 }}>
							<ListItemIcon sx={{ minWidth: 40, color: "success.main" }}>
								<SavingsIcon />
							</ListItemIcon>

							<ListItemText primary="Desconto" />

							<Typography color="success.main" fontWeight="bold">
								- {globalFormatCurrencyBRL(discount.toString())}
							</Typography>
						</ListItem>
					)}
				</List>

				<Divider />

				{/* TOTAL */}
				<Box
					sx={{
						display: "flex",
						justifyContent: "space-between",
						alignItems: "center",
						px: 2,
						pt: 2,
						bgcolor: theme.palette.grey[50],
					}}
				>
					<Box display="flex" alignItems="center" gap={1}>
						<ReceiptIcon color="primary" />
						<Typography variant="h6" fontWeight="bold">
							Valor Total
						</Typography>
					</Box>

					<Typography variant="h5" fontWeight="bold" color="primary">
						{globalFormatCurrencyBRL(animatedTotal.toString())}
					</Typography>
				</Box>

				{/* PARCELAMENTO */}
				{hasTotal && (
					<Box
						sx={{
							px: 2,
							pb: 2,
							textAlign: "right",
							bgcolor: theme.palette.grey[50],
						}}
					>
						<Typography variant="body2" fontStyle="italic">
							{installments}x de {globalFormatCurrencyBRL(animatedInstallment.toString())}
						</Typography>
					</Box>
				)}
			</CardContent>

			{/* Footer de ações */}
			<Box
				sx={{
					display: "flex",
					gap: 1,
					p: 2,
					bgcolor: theme.palette.grey[50],
				}}
			>
				<Button
					variant="outlined"
					fullWidth
					onClick={onSave}
					disabled={!canSave}
				>
					{isLoading ? <CircularProgress size={20} /> : saveLabel}
				</Button>

				<Button
					variant="contained"
					fullWidth
					onClick={onContract}
					disabled={!canContract}
				>
					Contratar
				</Button>
			</Box>
		</Card>
	);
};
